import { useState, useEffect, type FormEvent } from "react"
import { Head, Link, usePage, router } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { GreenScoreBadge } from "@/components/public/green-score-badge"
import ComparisonRow from "@/components/public/comparison-row"
import type { SeoMeta } from "@/types"
import type { GreenScore } from "@/types/public-data"

/* ------------------------------------------------------------------ */
/* Raw report shape from Rails (snake_case keys)                       */
/* ------------------------------------------------------------------ */

interface RawReport {
  postcode: {
    postcode: string
    latitude: number
    longitude: number
    admin_district: string
    constituency: string
    lsoa: string | null
    region: string
  } | null
  crime: {
    total_crimes: number
    month: string
    categories: Array<{ category: string; count: number }>
  } | null
  property: {
    sold_prices: Array<{ price: number; date: string; street: string }>
    price_index: Array<{ date: string; average_price: number }>
    current_average: number | null
  } | null
  flood: {
    stations: Array<{ label: string; river_name?: string; status?: string }>
    station_count: number
    has_active_warnings: boolean
  } | null
  amenities: {
    schools: Array<{ name: string; type?: string }>
    parks: Array<{ name: string }>
    restaurants: Array<{ name: string; cuisine?: string }>
    shops: number
    transport_stops: number
    cycleways: number
  } | null
  planning: {
    conservation_areas: number
    green_belt: boolean | number
    listed_buildings: number
  } | null
  health: {
    life_expectancy_male: number | null
    life_expectancy_female: number | null
    area_name: string
  } | null
  green_score: {
    total: number
    breakdown: {
      green_space: number
      flood_risk: number
      conservation: number
      walkability: number
      cycling: number
    }
  } | null
}

/* ------------------------------------------------------------------ */
/* Mapper                                                              */
/* ------------------------------------------------------------------ */

function mapGreenScore(raw: RawReport["green_score"]): GreenScore | null {
  if (!raw) return null
  return {
    total: raw.total,
    breakdown: {
      greenSpace: raw.breakdown.green_space,
      floodRisk: raw.breakdown.flood_risk,
      conservation: raw.breakdown.conservation,
      walkability: raw.breakdown.walkability,
      cycling: raw.breakdown.cycling,
    },
  }
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

interface PageProps {
  meta: SeoMeta
  postcodes: string[]
  reports: Record<string, RawReport | null>
  flash?: { error?: string }
}

export default function ComparePage({
  meta,
  postcodes,
  reports,
}: PageProps) {
  const pageProps = usePage().props as Record<string, unknown>
  const flash = pageProps.flash as { error?: string } | undefined
  const flashError = flash?.error

  const [inputA, setInputA] = useState(postcodes[0] ?? "")
  const [inputB, setInputB] = useState(postcodes[1] ?? "")
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const a = inputA.trim()
    const b = inputB.trim()
    if (!a || !b) return
    setLoading(true)
    router.get("/tools/compare", { a, b }, {
      onFinish: () => setLoading(false),
    })
  }

  function handleCopyLink() {
    void navigator.clipboard.writeText(window.location.href)
    setCopied(true)
  }

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(timer)
  }, [copied])

  const reportA = postcodes.length >= 2 ? reports[postcodes[0]] ?? null : null
  const reportB = postcodes.length >= 2 ? reports[postcodes[1]] ?? null : null
  const hasResults = reportA !== null && reportB !== null

  const greenScoreA = reportA ? mapGreenScore(reportA.green_score) : null
  const greenScoreB = reportB ? mapGreenScore(reportB.green_score) : null

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Head>

      {/* Hero */}
      <section className="bg-cream px-6 py-16 md:py-24">
        <div className="mx-auto max-w-4xl text-center">
          <div className="flex items-center justify-center gap-2">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
              Free tool
            </p>
            <span className="rounded-full bg-red/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red">
              Beta
            </span>
          </div>
          <h1 className="mt-4 font-display text-3xl font-bold tracking-tight text-charcoal sm:text-4xl md:text-5xl">
            Compare postcodes
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-4 max-w-lg text-lg text-slate">
            See how two UK postcodes stack up side by side: property prices, crime,
            green score, schools, and more.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="border-t border-stone px-6 py-10">
        <div className="mx-auto max-w-2xl">
          <form onSubmit={handleSubmit} className="flex flex-col items-center gap-4 sm:flex-row">
            <input
              type="text"
              value={inputA}
              onChange={(e) => setInputA(e.target.value)}
              placeholder="e.g. BS8 1AB"
              className="w-full rounded-lg border border-stone bg-white px-4 py-3 text-sm text-charcoal placeholder:text-slate/50 focus:border-charcoal focus:outline-none sm:flex-1"
            />
            <span className="text-sm font-bold text-slate">vs</span>
            <input
              type="text"
              value={inputB}
              onChange={(e) => setInputB(e.target.value)}
              placeholder="e.g. BS6 6SA"
              className="w-full rounded-lg border border-stone bg-white px-4 py-3 text-sm text-charcoal placeholder:text-slate/50 focus:border-charcoal focus:outline-none sm:flex-1"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-charcoal px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate disabled:opacity-60 sm:w-auto"
            >
              {loading ? "Comparing..." : "Compare"}
            </button>
          </form>

          {loading && (
            <p className="mt-4 text-center text-sm text-slate">
              This may take up to 30 seconds. We're fetching live data from seven
              government sources.
            </p>
          )}

          {flashError && (
            <p className="mt-4 text-center text-sm text-red">{flashError}</p>
          )}
        </div>
      </section>

      {/* Results */}
      {hasResults && (
        <section className="border-t border-stone bg-cream px-6 py-12">
          <div className="mx-auto max-w-3xl">
            {/* Collapsible search form */}
            <div className="mb-8">
              <button
                type="button"
                onClick={() => setSearchOpen((prev) => !prev)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-red transition-colors hover:text-deep-red"
              >
                <svg
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className={`size-4 transition-transform duration-200 ${searchOpen ? "rotate-180" : ""}`}
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"
                    clipRule="evenodd"
                  />
                </svg>
                Compare different postcodes
              </button>

              {searchOpen && (
                <form
                  onSubmit={handleSubmit}
                  className="mt-4 flex flex-col items-center gap-3 sm:flex-row"
                >
                  <input
                    type="text"
                    value={inputA}
                    onChange={(e) => setInputA(e.target.value)}
                    placeholder="e.g. BS8 1AB"
                    className="w-full rounded-lg border border-stone bg-white px-3 py-2 text-sm text-charcoal placeholder:text-slate/50 focus:border-charcoal focus:outline-none sm:flex-1"
                  />
                  <span className="text-xs font-bold text-slate">vs</span>
                  <input
                    type="text"
                    value={inputB}
                    onChange={(e) => setInputB(e.target.value)}
                    placeholder="e.g. BS6 6SA"
                    className="w-full rounded-lg border border-stone bg-white px-3 py-2 text-sm text-charcoal placeholder:text-slate/50 focus:border-charcoal focus:outline-none sm:flex-1"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-lg bg-charcoal px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate disabled:opacity-60 sm:w-auto"
                  >
                    {loading ? "Comparing..." : "Compare"}
                  </button>
                </form>
              )}
            </div>

            <ScrollReveal>
              {/* Comparison intro */}
              <p className="mb-6 text-center text-sm text-slate">
                Lower is better for price, crime, and flood risk. Higher is better
                for everything else. Data from seven UK government sources.
              </p>

              {/* Header: postcode A vs postcode B */}
              <div className="grid grid-cols-3 items-center gap-4">
                <div className="flex flex-col items-center text-center">
                  <p className="font-display text-xl font-bold text-charcoal">
                    {postcodes[0]}
                  </p>
                  {reportA?.postcode?.admin_district && (
                    <p className="mt-1 text-xs text-slate">
                      {reportA.postcode.admin_district}
                    </p>
                  )}
                  {greenScoreA && (
                    <div className="mt-3">
                      <GreenScoreBadge score={greenScoreA} size="sm" />
                    </div>
                  )}
                </div>

                <div className="text-center">
                  <span className="text-sm font-bold uppercase tracking-[0.15em] text-slate">
                    vs
                  </span>
                </div>

                <div className="flex flex-col items-center text-center">
                  <p className="font-display text-xl font-bold text-charcoal">
                    {postcodes[1]}
                  </p>
                  {reportB?.postcode?.admin_district && (
                    <p className="mt-1 text-xs text-slate">
                      {reportB.postcode.admin_district}
                    </p>
                  )}
                  {greenScoreB && (
                    <div className="mt-3">
                      <GreenScoreBadge score={greenScoreB} size="sm" />
                    </div>
                  )}
                </div>
              </div>

              {/* Comparison rows */}
              <div className="mt-8 rounded-xl border border-stone bg-white p-6">
                <ComparisonRow
                  label="Avg price"
                  valueA={reportA?.property?.current_average ?? null}
                  valueB={reportB?.property?.current_average ?? null}
                  format="currency"
                  higherIsBetter={false}
                />
                <div className="border-t border-stone/50" />

                <ComparisonRow
                  label="Crime"
                  valueA={reportA?.crime?.total_crimes ?? null}
                  valueB={reportB?.crime?.total_crimes ?? null}
                  format="number"
                  higherIsBetter={false}
                />
                <div className="border-t border-stone/50" />

                <ComparisonRow
                  label="Green score"
                  valueA={reportA?.green_score?.total ?? null}
                  valueB={reportB?.green_score?.total ?? null}
                  format="number"
                  higherIsBetter={true}
                />
                <div className="border-t border-stone/50" />

                <ComparisonRow
                  label="Schools"
                  valueA={reportA?.amenities?.schools?.length ?? null}
                  valueB={reportB?.amenities?.schools?.length ?? null}
                  format="number"
                  higherIsBetter={true}
                />
                <div className="border-t border-stone/50" />

                <ComparisonRow
                  label="Parks"
                  valueA={reportA?.amenities?.parks?.length ?? null}
                  valueB={reportB?.amenities?.parks?.length ?? null}
                  format="number"
                  higherIsBetter={true}
                />
                <div className="border-t border-stone/50" />

                <ComparisonRow
                  label="Restaurants"
                  valueA={reportA?.amenities?.restaurants?.length ?? null}
                  valueB={reportB?.amenities?.restaurants?.length ?? null}
                  format="number"
                  higherIsBetter={true}
                />
                <div className="border-t border-stone/50" />

                <ComparisonRow
                  label="Shops"
                  valueA={reportA?.amenities?.shops ?? null}
                  valueB={reportB?.amenities?.shops ?? null}
                  format="number"
                  higherIsBetter={true}
                />
                <div className="border-t border-stone/50" />

                <ComparisonRow
                  label="Flood stations"
                  valueA={reportA?.flood?.station_count ?? null}
                  valueB={reportB?.flood?.station_count ?? null}
                  format="number"
                  higherIsBetter={false}
                />
                <div className="border-t border-stone/50" />

                <ComparisonRow
                  label="Conservation areas"
                  valueA={reportA?.planning?.conservation_areas ?? null}
                  valueB={reportB?.planning?.conservation_areas ?? null}
                  format="number"
                  higherIsBetter={true}
                />
                <div className="border-t border-stone/50" />

                <ComparisonRow
                  label="Life expectancy (M)"
                  valueA={reportA?.health?.life_expectancy_male ?? null}
                  valueB={reportB?.health?.life_expectancy_male ?? null}
                  format="number"
                  higherIsBetter={true}
                />
                <div className="border-t border-stone/50" />

                <ComparisonRow
                  label="Life expectancy (F)"
                  valueA={reportA?.health?.life_expectancy_female ?? null}
                  valueB={reportB?.health?.life_expectancy_female ?? null}
                  format="number"
                  higherIsBetter={true}
                />
              </div>

              {/* Actions */}
              <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-stone bg-white px-5 py-2.5 text-sm font-medium text-charcoal transition-colors hover:bg-cream sm:w-auto"
                >
                  {copied ? (
                    <>
                      <svg viewBox="0 0 20 20" fill="currentColor" className="size-4">
                        <path
                          fillRule="evenodd"
                          d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 20 20" fill="currentColor" className="size-4">
                        <path d="M12.232 4.232a2.5 2.5 0 0 1 3.536 3.536l-1.225 1.224a.75.75 0 0 0 1.061 1.06l1.224-1.224a4 4 0 0 0-5.656-5.656l-3 3a4 4 0 0 0 .225 5.865.75.75 0 0 0 .977-1.138 2.5 2.5 0 0 1-.142-3.667l3-3Z" />
                        <path d="M11.603 7.963a.75.75 0 0 0-.977 1.138 2.5 2.5 0 0 1 .142 3.667l-3 3a2.5 2.5 0 0 1-3.536-3.536l1.225-1.224a.75.75 0 0 0-1.061-1.06l-1.224 1.224a4 4 0 1 0 5.656 5.656l3-3a4 4 0 0 0-.225-5.865Z" />
                      </svg>
                      Share link
                    </>
                  )}
                </button>
                <Link
                  href={`/tools/postcode-report/${postcodes[0].replace(/\s+/g, "")}`}
                  className="inline-flex w-full items-center justify-center rounded-lg bg-charcoal px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-slate sm:w-auto"
                >
                  Full report: {postcodes[0]}
                </Link>
                <Link
                  href={`/tools/postcode-report/${postcodes[1].replace(/\s+/g, "")}`}
                  className="inline-flex w-full items-center justify-center rounded-lg bg-charcoal px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-slate sm:w-auto"
                >
                  Full report: {postcodes[1]}
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* Data sources */}
      <section className="border-t border-stone px-6 py-8">
        <div className="mx-auto max-w-4xl">
          <p className="text-center text-xs text-slate">
            Data sources: postcodes.io, Police.uk, HM Land Registry, Environment Agency,
            OpenStreetMap (Overpass), Planning Data (DLUHC), PHE Fingertips.
          </p>
        </div>
      </section>
    </PublicLayout>
  )
}
